/**
 * 룸 카메라 규칙(P2-S5 · SR-SOC-04 따라오기·관전 · SR-TCH-02 칠판 자동 카메라) — 렌더러는 걷는 점(setViewpoint)만 받고
 * 부드럽게 따라간다(지수 감쇠 · 강성 4 → 1초에 98% — 전환 ≤1초). 어디를 볼지는 여기서 정한다(순수 함수 · 시험 가능).
 *  - 기계 자리 = 차체(첫 몸체) x·z
 *  - 밀어내기 링에 든 기계 = 지금 경기(meta.mode.arenas 의 중심·반지름 — 이름은 명부에서)
 *  - 칠판 자동: 움직이는 기계가 가장 많이 모인 곳(반경 4 m 무리) — 없으면 전체
 */
import { DEFAULT_GALLERY_CAMERA } from "@picnic-field/renderer/galleryMap";
import { sectionFocus } from "@picnic-field/renderer/relayMap";
import type { SectionLike } from "@picnic-field/renderer/relayMap";
import type { Focus, Arena, RoomMeta } from "../types";

/** 기계 차체 자리(없으면 null) */
export function machinePos(
  meta: RoomMeta,
  transforms: Float32Array,
  machine: number,
): { x: number; z: number } | null {
  const m = meta.machines.find((x) => x.machine === machine);
  if (!m) return null;
  const o = m.bodyStart * 7;
  const x = transforms[o];
  const z = transforms[o + 2];
  return x === undefined || z === undefined ? null : { x, z };
}

/** 차체 중심 높이(m) — 링 위면 링 두께만큼 높다 */
export function chassisY(meta: RoomMeta, transforms: Float32Array, machine: number): number | null {
  const m = meta.machines.find((x) => x.machine === machine);
  const y = m ? transforms[m.bodyStart * 7 + 1] : undefined;
  return y === undefined ? null : y;
}

/**
 * 따라가는 기계(내 기계 · SR-SOC-04 친구) 카메라 자리 — 기계가 화면 가운데에 오게 한다.
 * 전시관 카메라(galleryCameraTarget)는 선 자리보다 `ahead`(0.45 m) 앞을 보므로 선 자리의 기계는 화면 아래쪽(≈70%)에 그려진다 —
 * 룸에서는 그 자리를 리모컨(SE 천천히·빨리 단추 · 가운데 아래)이 덮어 내 기계가 보이지 않았다(2026-09-29 밀어내기 실스택 헤드 실행).
 * 걷는 점을 ahead 만큼 뒤로 두면 카메라가 기계 자체를 본다(시선 중심 = 기계).
 */
export function followFocus(
  meta: RoomMeta,
  transforms: Float32Array,
  machine: number,
): Focus | null {
  const p = machinePos(meta, transforms, machine);
  if (!p) return null;
  const y = chassisY(meta, transforms, machine);
  return {
    x: p.x,
    z: p.z - (DEFAULT_GALLERY_CAMERA.ahead ?? 0),
    back: FOLLOW_BACK,
    ...(y !== null ? { lookUp: y + LOOK_LIFT } : {}),
  };
}

/**
 * 화면 회전(2026-10-11 · 칠판 뷰 · 학생 룸) — 바라보는 점(걷는 점 + ahead · 렌더러 galleryCameraTarget)은 그대로 두고 보는 방향만 dh(rad) 돌린다.
 * 그래서 내 기계(또는 고른 기계)가 화면 가운데에 남은 채 카메라가 그 둘레를 돈다.
 */
export function turnFocus(f: Focus, dh: number): Focus {
  if (!dh) return f;
  const ahead = DEFAULT_GALLERY_CAMERA.ahead ?? 0;
  const h0 = f.heading ?? 0;
  const h = h0 + dh;
  const lx = f.x + Math.sin(h0) * ahead;
  const lz = f.z + Math.cos(h0) * ahead;
  return { ...f, x: lx - Math.sin(h) * ahead, z: lz - Math.cos(h) * ahead, heading: h };
}

/** 밀어내기 내 경기 뒤 시점 거리(m) — 전시관 기본(1.7)보다 훨씬 가까이(작품 20~40 cm가 화면 세로 ≈⅓) */
export const CHASE_BACK = 0.7;
/** 룸에서 내 기계(로비 · 친구 따라가기)를 따라갈 때 거리(m) — 주변도 조금 보이게 CHASE_BACK 보다는 멀리 */
export const FOLLOW_BACK = 0.9;
/** 바라보는 점 = 차체 중심 높이 + 이만큼(m) — 가까이서도 기계가 화면 가운데(리모컨은 아래) */
export const LOOK_LIFT = 0.05;

/** 차체 모형 +z 축을 바닥에 비춘 방위(rad · 0 = +z · +x 쪽이 +) — 거의 서 있으면(수평 성분 없음) null */
export function chassisHeading(
  meta: RoomMeta,
  transforms: Float32Array,
  machine: number,
): number | null {
  const m = meta.machines.find((x) => x.machine === machine);
  if (!m) return null;
  const o = m.bodyStart * 7;
  const [qx, qy, qz, qw] = [
    transforms[o + 3],
    transforms[o + 4],
    transforms[o + 5],
    transforms[o + 6],
  ];
  if (qx === undefined || qy === undefined || qz === undefined || qw === undefined) return null;
  // q · (0,0,1)
  const fx = 2 * (qx * qz + qw * qy);
  const fz = 1 - 2 * (qx * qx + qy * qy);
  return Math.hypot(fx, fz) < 1e-3 ? null : Math.atan2(fx, fz);
}

/**
 * 밀어내기 경기 중 내 기계 뒤 시점 — 카메라가 기계 뒤에서 기계가 보는 쪽(상대)을 본다.
 * 모형의 앞이 +z 인지 −z 인지는 셸이 모르므로 `flip` 으로 받는다({@link chaseFlip} — 경기 시작 때 링 가운데를 보는 쪽).
 * 걷는 점은 followFocus 처럼 ahead 만큼 뒤로 — 시선 중심이 기계 자체(리모컨에 가리지 않게).
 */
export function chaseFocus(
  meta: RoomMeta,
  transforms: Float32Array,
  machine: number,
  flip: boolean,
): Focus | null {
  const p = machinePos(meta, transforms, machine);
  const h0 = chassisHeading(meta, transforms, machine);
  if (!p || h0 === null) return null;
  const heading = flip ? h0 + Math.PI : h0;
  const ahead = DEFAULT_GALLERY_CAMERA.ahead ?? 0;
  const y = chassisY(meta, transforms, machine);
  return {
    x: p.x - Math.sin(heading) * ahead,
    z: p.z - Math.cos(heading) * ahead,
    heading,
    back: CHASE_BACK,
    ...(y !== null ? { lookUp: y + LOOK_LIFT } : {}),
  };
}

/** 승리 확대(2026-10-11) — 차체에서 이만큼(m · 내 기계 뒤 CHASE_BACK 보다 가까이) · 초당 도는 각(rad) */
export const WIN_BACK = 0.45;
export const WIN_ORBIT = 0.9;

/**
 * 밀어내기 승리 — 이긴 기계를 가까이서 천천히 돌며 본다(결과 뒤 몇 초 · 대기석으로 옮겨져도 따라간다).
 * 처음 방향은 기계 뒤(chaseFocus 와 같다) · t 초마다 WIN_ORBIT 만큼 돈다(렌더러 감쇠가 부드럽게 잇는다).
 */
export function winFocus(
  meta: RoomMeta,
  transforms: Float32Array,
  machine: number,
  t: number,
  heading0: number,
): Focus | null {
  const p = machinePos(meta, transforms, machine);
  if (!p) return null;
  const heading = heading0 + t * WIN_ORBIT;
  const ahead = DEFAULT_GALLERY_CAMERA.ahead ?? 0;
  const y = chassisY(meta, transforms, machine);
  return {
    x: p.x - Math.sin(heading) * ahead,
    z: p.z - Math.cos(heading) * ahead,
    heading,
    back: WIN_BACK,
    ...(y !== null ? { lookUp: y + LOOK_LIFT } : {}),
  };
}

/** 모형 +z 가 링 가운데 반대쪽이면 true(그 모형은 −z 가 앞) — 경기 시작 자리(마주 보기)에서 한 번 정한다 */
export function chaseFlip(
  meta: RoomMeta,
  transforms: Float32Array,
  machine: number,
  arena: Arena,
): boolean | null {
  const p = machinePos(meta, transforms, machine);
  const h = chassisHeading(meta, transforms, machine);
  if (!p || h === null) return null;
  const tx = arena.center[0] - p.x;
  const tz = arena.center[2] - p.z;
  return Math.sin(h) * tx + Math.cos(h) * tz < 0;
}

/** 내 기계에 가장 가까운 링(반지름 + 0.3 m 안) — 경기 중인 링 */
export function arenaOf(meta: RoomMeta, transforms: Float32Array, machine: number): Arena | null {
  const p = machinePos(meta, transforms, machine);
  if (!p) return null;
  let best: Arena | null = null;
  let bestD = Infinity;
  for (const a of arenasOf(meta)) {
    const d = Math.hypot(p.x - a.center[0], p.z - a.center[2]);
    if (d <= a.radius + 0.3 && d < bestD) {
      best = a;
      bestD = d;
    }
  }
  return best;
}

export function arenasOf(meta: RoomMeta): Arena[] {
  const v = meta.mode.arenas;
  return (Array.isArray(v) ? v : [])
    .filter(
      (a): a is Arena =>
        !!a &&
        typeof a === "object" &&
        Array.isArray((a as Arena).center) &&
        typeof (a as Arena).radius === "number",
    )
    .map((a, i) => ({ ...a, index: typeof a.index === "number" ? a.index : i }));
}

/**
 * 링 안의 기계(지금 경기) — 차체가 반지름(+여유 0.3 m) 안이면 선수. 둘 이상이면 경기 중.
 * 서버는 경기 시작을 당사자에게만 알리므로(ev match) 대기자·칠판은 자리로 안다.
 */
export function arenaPlayers(
  meta: RoomMeta,
  transforms: Float32Array,
): { arena: Arena; machines: number[] }[] {
  const out: { arena: Arena; machines: number[] }[] = [];
  for (const a of arenasOf(meta)) {
    const ms: number[] = [];
    for (const m of meta.machines) {
      const p = machinePos(meta, transforms, m.machine);
      if (p && Math.hypot(p.x - a.center[0], p.z - a.center[2]) <= a.radius + 0.3)
        ms.push(m.machine);
    }
    if (ms.length >= 2) out.push({ arena: a, machines: ms.slice(0, 2) });
  }
  return out;
}

/** 관전(대기자) — 가장 앞(번호 작은) 경기 링 한가운데를 조금 물러서서 */
export function arenaFocus(arena: Arena): Focus {
  return { x: arena.center[0], z: arena.center[2], back: Math.max(3.5, arena.radius * 1.6 + 1.5) };
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** 움직이는 기계 기준(m/s) */
export const MOVING_MPS = 0.08;
/** 무리 반경(m) */
export const CLUSTER_M = 4;

/**
 * 칠판 자동 카메라 — 두 프레임 사이에 움직인 기계가 가장 많이 모인 곳. 움직임이 없으면 모든 기계의 가운데를 넓게.
 * `only` 가 있으면 그 기계만 센다(온라인 멤버).
 */
export function densityFocus(
  meta: RoomMeta,
  prev: Float32Array | null,
  cur: Float32Array,
  dtS: number,
  only?: ReadonlySet<number>,
): Focus {
  const pts: { x: number; z: number; moving: boolean }[] = [];
  for (const m of meta.machines) {
    if (only && !only.has(m.machine)) continue;
    const p = machinePos(meta, cur, m.machine);
    if (!p) continue;
    const q = prev ? machinePos(meta, prev, m.machine) : null;
    const speed = q && dtS > 0 ? Math.hypot(p.x - q.x, p.z - q.z) / dtS : 0;
    pts.push({ ...p, moving: speed > MOVING_MPS });
  }
  if (!pts.length) return { x: 0, z: 4, back: 12 };
  const movers = pts.filter((p) => p.moving);
  const spreadOf = (xs: { x: number; z: number }[], c: { x: number; z: number }) =>
    Math.max(0, ...xs.map((p) => Math.hypot(p.x - c.x, p.z - c.z)));
  const centroid = (xs: { x: number; z: number }[]) => ({
    x: xs.reduce((a, p) => a + p.x, 0) / xs.length,
    z: xs.reduce((a, p) => a + p.z, 0) / xs.length,
  });
  if (!movers.length) {
    const c = centroid(pts);
    return { ...c, back: clamp(spreadOf(pts, c) * 1.3 + 3, 8, 18) };
  }
  // 가장 이웃이 많은 움직이는 기계를 씨앗으로(같으면 앞 번호)
  let best = movers[0]!;
  let bestN = -1;
  for (const p of movers) {
    const n = movers.filter((q) => Math.hypot(p.x - q.x, p.z - q.z) <= CLUSTER_M).length;
    if (n > bestN) {
      best = p;
      bestN = n;
    }
  }
  const group = movers.filter((q) => Math.hypot(best.x - q.x, best.z - q.z) <= CLUSTER_M);
  const c = centroid(group);
  return { ...c, back: clamp(spreadOf(group, c) * 1.6 + 8, 9, 18) };
}

/** 하이라이트 추적(칠판) — 그 기계 가까이 */
export const HIGHLIGHT_BACK = 6;

/** P3-S2 칠판 릴레이 변형 — 넘겨받기 순간 확대 배율 · 유지 시간 · 구간 정보가 없을 때 기계를 보는 거리 */
export const RELAY_BACK = 7;
export const RELAY_HANDOFF_ZOOM = 0.65;
export const RELAY_HANDOFF_MS = 1500;

/** meta.mode.sections(룸 FullState — origin [x,z] · yaw) 중 sectionFocus 가 쓰는 모양만 */
export function sectionsOf(meta: RoomMeta): SectionLike[] {
  const xs = Array.isArray(meta.mode.sections) ? (meta.mode.sections as unknown[]) : [];
  return xs.flatMap((v) => {
    const o = v as { origin?: unknown; yaw?: unknown; exitBox?: SectionLike["exitBox"] };
    const org = o?.origin;
    if (!Array.isArray(org) || typeof org[0] !== "number" || typeof org[1] !== "number") return [];
    return [
      {
        origin: [org[0], org[1]] as const,
        yaw: typeof o.yaw === "number" ? o.yaw : 0,
        ...(o.exitBox ? { exitBox: o.exitBox } : {}),
      },
    ];
  });
}

/**
 * 릴레이 자동 카메라(v07-15) — 구간 i 초점(렌더러 sectionFocus · 전시관 뷰는 yaw 없이 target · distance).
 * 넘겨받기(ev done) 직후 잠깐은 끝난 구간을 더 가까이. 구간 정보가 없으면(P3-S1 룸) 그 구간 기계 자리.
 */
export function relayFocus(
  meta: RoomMeta,
  sections: readonly SectionLike[],
  transforms: Float32Array,
  stage: number | null,
  machine: number | null,
  handoff: boolean,
): Focus | null {
  if (stage === null) return null;
  const zoom = handoff ? RELAY_HANDOFF_ZOOM : 1;
  const f = sectionFocus(sections, stage);
  if (f) return { x: f.target[0], z: f.target[2], back: f.distance * zoom };
  const p = machine !== null ? machinePos(meta, transforms, machine) : null;
  return p ? { ...p, back: RELAY_BACK * zoom } : null;
}
